export type ClientDiagnosticCategory = "stack_overflow" | "dom_mutation";

export interface ClientExceptionDiagnostic {
  category: ClientDiagnosticCategory;
  error_name: string;
  route: string;
  source_path: string | null;
  line_number: number | null;
  column_number: number | null;
  document_ready_state: DocumentReadyState;
  visibility_state: DocumentVisibilityState;
  body_child_count: number;
  translated_dom_detected: boolean;
  webdriver: boolean;
}

type CaptureDiagnostic = (diagnostic: ClientExceptionDiagnostic) => void;

let installed = false;

export function classifyClientException(message: string): ClientDiagnosticCategory | null {
  if (/maximum call stack size exceeded/i.test(message)) return "stack_overflow";
  if (
    /failed to execute '(?:insertBefore|removeChild)' on 'Node'/i.test(message) ||
    /node (?:before which|to be removed).+not a child/i.test(message)
  ) {
    return "dom_mutation";
  }
  return null;
}

function sourcePath(filename: string | undefined): string | null {
  if (!filename) return null;
  try {
    return new URL(filename, window.location.origin).pathname;
  } catch {
    return null;
  }
}

function translatedDomDetected(): boolean {
  const root = document.documentElement;
  return (
    root.classList.contains("translated-ltr") ||
    root.classList.contains("translated-rtl") ||
    Boolean(document.querySelector(".goog-te-banner-frame, [class*='translated-']"))
  );
}

/**
 * Enrich only the two unresolved browser-error families. The diagnostic omits
 * query strings, DOM content, stack text, user input, and full user-agent data.
 */
export function installClientExceptionDiagnostics(capture: CaptureDiagnostic): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const seen = new Set<string>();

  const record = (input: {
    message: string;
    errorName?: string;
    filename?: string;
    line?: number;
    column?: number;
  }) => {
    const category = classifyClientException(input.message);
    if (!category) return;
    const signature = `${category}:${window.location.pathname}:${input.filename ?? ""}:${input.line ?? 0}:${input.column ?? 0}`;
    if (seen.has(signature) || seen.size >= 20) return;
    seen.add(signature);
    capture({
      category,
      error_name: input.errorName || "Error",
      route: window.location.pathname,
      source_path: sourcePath(input.filename),
      line_number: input.line ?? null,
      column_number: input.column ?? null,
      document_ready_state: document.readyState,
      visibility_state: document.visibilityState,
      body_child_count: document.body?.childElementCount ?? 0,
      translated_dom_detected: translatedDomDetected(),
      webdriver: Boolean(navigator.webdriver),
    });
  };

  window.addEventListener("error", (event) => {
    record({
      message: event.message || String(event.error),
      errorName: event.error instanceof Error ? event.error.name : undefined,
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    record({
      message: reason instanceof Error ? reason.message : String(reason),
      errorName: reason instanceof Error ? reason.name : "UnhandledRejection",
    });
  });
}
